import React, {useCallback, useEffect} from 'react';

import {LABELING_STRATEGY_TO_TEXT, ORDERED_LABELING_STRATEGIES} from 'constants/RegistryConstants';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {maybeGetValueFromConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {
  SchemaLabelingStrategiesQuery,
  SchemaLabelingStrategiesQueryVariables,
} from 'registry/labeling-strategies/__generated/SchemaLabelingStrategiesQuery';
import {SCHEMA_LABELING_STRATEGY_INPUT_QUERY} from 'registry/labeling-strategies/SchemaLabelingStrategyInput';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {CenteredSpinner} from 'ui-library/Spinner';

export const SELECT_PROTEIN_SCHEMA_FIRST_TEXT = 'Select a protein schema first';
export const SELECT_NAMING_STRATEGY_TEXT = 'Select a naming strategy';

export function getAutomaticNamingStrategyText(automaticNamingStrategy: RegistryLabelingStrategy): string {
  return `Automatic: ${LABELING_STRATEGY_TO_TEXT[automaticNamingStrategy]}`;
}

/**
 * Protein NDC naming strategy dropdown limited to the strategies the selected protein schema allows.
 *
 * A blank value makes the step pick the strategy from the schema's name template: replace names from the
 * template when there is one, otherwise replace names with the registry ID. The blank option is only
 * offered when the schema allows that strategy; otherwise the first allowed strategy is selected.
 */
export default function ProteinNdcNamingStrategyEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const proteinSchemaId =
    (maybeGetValueFromConfig(config, ['proteinSchemaId']) as string | null | undefined) ?? null;
  const namingStrategy =
    (maybeGetValueFromConfig(config, ['namingStrategy']) as RegistryLabelingStrategy | null | undefined) ??
    null;

  const result = useSkippableBenchlingQuery<
    SchemaLabelingStrategiesQuery,
    SchemaLabelingStrategiesQueryVariables
  >(SCHEMA_LABELING_STRATEGY_INPUT_QUERY, {
    variables: {tagSchemaId: proteinSchemaId ?? ''},
    skip: proteinSchemaId == null,
  });
  const schema =
    result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.tagSchema)
      ? result.data.tagSchema
      : null;
  const schemaLabelingStrategies = schema?.labelingStrategies ?? null;
  const automaticNamingStrategy =
    schema == null
      ? null
      : schema.nameTemplateParts.length > 0
        ? RegistryLabelingStrategy.REPLACE_NAMES_FROM_PARTS
        : RegistryLabelingStrategy.DELETE_NAMES;
  const allowedAutomaticNamingStrategy =
    automaticNamingStrategy != null && (schemaLabelingStrategies ?? []).includes(automaticNamingStrategy)
      ? automaticNamingStrategy
      : null;
  const options = ORDERED_LABELING_STRATEGIES.filter((strategy): strategy is RegistryLabelingStrategy =>
    (schemaLabelingStrategies ?? []).includes(strategy as RegistryLabelingStrategy)
  );
  const fallbackNamingStrategy = allowedAutomaticNamingStrategy != null ? null : (options[0] ?? null);

  const handleSelect = useCallback(
    (newNamingStrategy: RegistryLabelingStrategy | null) => {
      onChangeConfig(config, ['namingStrategy'], newNamingStrategy);
    },
    [config, onChangeConfig]
  );

  // A strategy the schema doesn't allow fails the step. That includes a blank value when the schema
  // doesn't allow the automatic strategy, and a choice left over from a previously selected schema.
  useEffect(() => {
    if (
      !disabled &&
      schemaLabelingStrategies != null &&
      (namingStrategy == null || !schemaLabelingStrategies.includes(namingStrategy)) &&
      namingStrategy !== fallbackNamingStrategy
    ) {
      handleSelect(fallbackNamingStrategy);
    }
  }, [disabled, namingStrategy, schemaLabelingStrategies, fallbackNamingStrategy, handleSelect]);

  if (result.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  if (result.type === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load naming strategies." />;
  }

  const displayName =
    namingStrategy != null
      ? LABELING_STRATEGY_TO_TEXT[namingStrategy]
      : proteinSchemaId == null
        ? SELECT_PROTEIN_SCHEMA_FIRST_TEXT
        : allowedAutomaticNamingStrategy != null
          ? getAutomaticNamingStrategyText(allowedAutomaticNamingStrategy)
          : SELECT_NAMING_STRATEGY_TEXT;

  return (
    <SelectDropdown
      ariaLabel="Naming strategy"
      disabled={Boolean(disabled) || proteinSchemaId == null}
      displayName={displayName}
      menu={
        <Menu scrollable={true} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={namingStrategy}>
          {allowedAutomaticNamingStrategy != null ? (
            <Option value={null} onSelect={handleSelect}>
              {getAutomaticNamingStrategyText(allowedAutomaticNamingStrategy)}
            </Option>
          ) : null}
          {options.map((strategy) => (
            <Option value={strategy} onSelect={handleSelect} key={strategy}>
              {LABELING_STRATEGY_TO_TEXT[strategy]}
            </Option>
          ))}
        </Menu>
      }
    />
  );
}
